import { motion } from 'framer-motion';
import { ShieldCheck, Lock, ShieldAlert } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

/**
 * LockdownPage — full-screen "Under Security Update" landing rendered
 * automatically by `LockdownInterceptor` whenever the backend returns
 * a 503 with `X-Lockdown: 1`.
 *
 *   - Premium dark navy + indigo glow (matches the new Soft-Contrast palette)
 *   - Animated pulsing shield in the centre (Framer Motion)
 *   - Translates via `useLanguage().localize` so all 7 languages are covered
 *   - When `autoTriggered=true`, switches to the "active cyber attack"
 *     reassurance copy (data is protected, AI is responding).
 *   - No actions — the only escape is the page auto-reloading every 30 s
 *     when the lockdown clears.
 */
const LockdownPage = ({ onCheck, autoTriggered = false }) => {
  const { localize } = useLanguage();

  // ── Copy variants ──────────────────────────────────────────────────
  const headline   = autoTriggered
    ? localize({
        ar: 'تتعرّض المنصة حالياً لهجوم سيبراني — يرجى الانتظار',
        en: 'Platform under cyber-attack — please hold',
      })
    : localize({ ar: 'تحديث أمني قيد التنفيذ', en: 'Security Update in Progress' });

  const body = autoTriggered
    ? localize({
        ar: 'رصد الحارس الذكي نمط هجوم كثيف وقام بتفعيل القفل التلقائي. بياناتكم محمية بالكامل وفريق الأمان يعمل على المعالجة الآن.',
        en: 'Our AI Watchdog detected a heavy attack pattern and engaged automatic lockdown. Your data is fully protected and our security team is responding now.',
      })
    : localize({
        ar: 'منصة Trust Ed Techno تخضع حالياً لتحديث أمني مجدوَل لحماية بياناتك. ستعود الخدمة خلال دقائق قليلة.',
        en: 'Trust Ed Techno is undergoing a scheduled security update to protect your data. We\'ll be back online in just a few minutes.',
      });

  const reassurance = autoTriggered
    ? localize({
        ar: 'تم حماية بياناتكم',
        en: 'Your data is protected',
      })
    : null;

  // Color theme switches subtly to "alert mode" without panic
  const accent = autoTriggered ? '#fbbf24'  : '#a5b4fc';   // amber vs indigo
  const glow   = autoTriggered ? '#dc2626'  : '#6366F1';   // crimson vs indigo
  const Icon   = autoTriggered ? ShieldAlert : ShieldCheck;

  return (
    <div
      className="fixed inset-0 z-[400] flex flex-col items-center justify-center overflow-hidden
        bg-[radial-gradient(ellipse_at_center,#0F172A_0%,#020617_100%)] text-white"
      role="alert"
      aria-live="assertive"
      data-testid="lockdown-page"
      data-auto-triggered={autoTriggered ? 'true' : 'false'}
    >
      {/* Background glow — softer indigo (normal) or warm crimson pulse (attack) */}
      <motion.div
        className="absolute pointer-events-none rounded-full blur-3xl"
        style={{ width: 520, height: 520, background: glow, opacity: 0.18 }}
        animate={{
          scale: autoTriggered ? [0.85, 1.12, 0.85] : [0.9, 1.05, 0.9],
          opacity: autoTriggered ? [0.16, 0.30, 0.16] : [0.14, 0.22, 0.14],
        }}
        transition={{
          duration: autoTriggered ? 2.4 : 4.5,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Pulsing shield */}
      <motion.div
        className="relative w-32 h-32 md:w-40 md:h-40 rounded-2xl
          bg-white/[0.04] ring-1 ring-white/10 backdrop-blur flex items-center justify-center
          shadow-[0_24px_70px_rgba(0,0,0,0.5)]"
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
      >
        <Icon
          className="w-14 h-14 md:w-16 md:h-16"
          strokeWidth={1.5}
          style={{ color: accent }}
        />
        <motion.span
          className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full ring-4 ring-slate-900 flex items-center justify-center"
          style={{ background: autoTriggered ? '#dc2626' : '#f59e0b' }}
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: autoTriggered ? 0.9 : 1.4, repeat: Infinity }}
        >
          <Lock className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
        </motion.span>
      </motion.div>

      {/* Brand line */}
      <p
        className="mt-8 text-[10px] md:text-[11px] font-bold tracking-[0.42em] uppercase"
        style={{ color: autoTriggered ? '#fcd34d' : '#a5b4fc' }}
      >
        {autoTriggered ? 'Trust Ed Techno · Sovereign Shield' : 'Trust Ed Techno'}
      </p>

      {/* Headline */}
      <h1 className="mt-2 text-2xl sm:text-3xl md:text-5xl font-extrabold tracking-tight text-center px-6 max-w-3xl leading-tight">
        {headline}
      </h1>

      {/* Reassuring body */}
      <p className="mt-4 text-sm md:text-base font-medium text-white/65 max-w-xl text-center px-6 leading-relaxed">
        {body}
      </p>

      {/* Big reassurance pill — only in attack mode */}
      {reassurance && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full
            bg-emerald-500/15 ring-1 ring-emerald-400/40 text-emerald-200 text-xs font-extrabold tracking-wide"
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          {reassurance}
        </motion.div>
      )}

      {/* Animated dots */}
      <div className="flex items-center gap-2 mt-7" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="w-2 h-2 rounded-full"
            style={{ background: accent }}
            animate={{ opacity: [0.25, 1, 0.25], y: [0, -3, 0] }}
            transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.18 }}
          />
        ))}
      </div>

      {/* Optional manual "check status" button */}
      {onCheck && (
        <button
          type="button"
          onClick={onCheck}
          className="mt-8 inline-flex items-center gap-2 px-5 py-2 rounded-full
            bg-white/[0.06] hover:bg-white/[0.12] ring-1 ring-white/15
            text-xs font-bold tracking-wide text-white/85 transition-colors"
          data-testid="lockdown-check-status"
        >
          {localize({ ar: 'تحقق من الحالة الآن', en: 'Check status now' })}
        </button>
      )}

      {/* Footer */}
      <p className="absolute bottom-5 text-[10px] font-mono text-white/30 tracking-widest">
        TRUSTEDTECHNO.COM · STATUS · {new Date().toUTCString().slice(17, 22)} UTC
      </p>
    </div>
  );
};

export default LockdownPage;
